import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Project } from 'src/app/core/objects/project';
import { CountriesService } from 'src/app/core/services/countries.service';
import { ProjectService } from 'src/app/core/services/project.service';
import { WorkSpace, WorkSpaceStop } from 'src/app/core/types';

@Component({
    selector: 'gl-multi-stop-edit',
    templateUrl: './multi-stop-edit.component.html',
    styleUrls: ['./multi-stop-edit.component.scss'],
    standalone: false
})
export class MultiStopEditComponent implements OnInit, AfterViewInit {
  @Input() public position: DOMRect;
  @Input() public currentWorkSpaceId: number;
  @Input() public savedStops: WorkSpaceStop[];
  @Output() public closeWindow = new EventEmitter<any>();
  @Output() public submitStops: EventEmitter<WorkSpaceStop[]> = new EventEmitter<WorkSpaceStop[]>();
  @Input() public isComplete = false;
  public form: UntypedFormGroup;
  public addressForm: UntypedFormGroup;
  public workSpace: WorkSpace;
  public project: Project;
  public existingStopsArray: { controlIndex: string; stop: WorkSpaceStop }[] = [];
  public selectedStopIndex: number;
  // public eMapa = false;
  private activeAddresses: string[] = [];
  public countries: string[] = [];
  private currentLang: string;

  constructor(
    private projectService: ProjectService,
    private translate: TranslateService,
    private countriesService: CountriesService,
  ) {
    this.currentLang = this.translate.currentLang;
    this.countries = this.countriesService.getCountries(this.currentLang);
  }

  public onSearch(value: string): void {
    const allCountries = this.countriesService.getCountries(this.currentLang);
    this.countries = this.countriesService.filterCountries(allCountries, value);
  }

  // public onEmapa(): void {
  //   this.eMapa = !this.eMapa;
  //   this.projectService.isEmapa = this.eMapa;
  // }

  public ngOnInit(): void {
    // this.eMapa = this.projectService.isEmapa;
    if (this.currentWorkSpaceId && !this.savedStops) {
      this.projectService.project.subscribe((project: Project): void => {
        this.project = project;
        this.workSpace = project.workSpaces.find(
          (space: WorkSpace): boolean => space.id === this.currentWorkSpaceId,
        );
      });
      if (this.workSpace.stops?.length > 0) {
        this.form = new UntypedFormGroup({});
        this.addressForm = new UntypedFormGroup({});
        this.workSpace.stops.forEach((stop: WorkSpace): void => {
          this.addAnotherPoint({ name: stop.name, id: stop.id } as WorkSpaceStop);
        });
        this.selectedStopIndex = 0;
      } else {
        this.form = this.generateForm();
        this.addressForm = this.generateAddressForm();
      }
    } else if (this.savedStops) {
      this.form = new UntypedFormGroup({});
      this.addressForm = new UntypedFormGroup({});
      this.savedStops.forEach((stop: WorkSpaceStop): void => {
        this.addAnotherPoint(stop);
      });
    } else {
      this.form = this.generateForm();
      this.addressForm = this.generateAddressForm();
    }
  }

  public isAddressOn(index: string): boolean {
    return this.activeAddresses.includes(index) || this.isComplete;
  }

  public close(): void {
    this.closeWindow.emit();
  }

  public changeStop(stopIndex: number): void {
    this.selectedStopIndex = stopIndex;
  }

  public addAnotherPoint(stop?: WorkSpaceStop): void {
    const controlsKeys: string[] = this.objectKeys(this.form.controls);
    const controlIndex: string =
      controlsKeys.length > 0 ? (+controlsKeys[controlsKeys.length - 1] + 1).toString() : '0';
    const control: UntypedFormControl = new UntypedFormControl(stop?.name ? stop.name : '', [
      Validators.maxLength(255),
      Validators.required,
    ]);

    if (stop) {
      this.existingStopsArray.push({ controlIndex, stop });
    }
    this.form.addControl(controlIndex, control);
    const countryValue: string = stop?.country ? stop.country : '';
    const cityValue: string = stop?.city ? stop.city : '';
    const postalCodeValue: string = stop?.postalCode ? stop.postalCode : '';
    const streetValue: string = stop?.address ? stop.address : '';
    if (this.isComplete) {
      this.addressForm.addControl(
        controlIndex,
        new UntypedFormGroup({
          country: new UntypedFormControl(countryValue, [
            Validators.minLength(1),
            Validators.maxLength(255),
            Validators.required,
          ]),
          city: new UntypedFormControl(cityValue, [
            Validators.minLength(1),
            Validators.maxLength(255),
            Validators.required,
          ]),
          postalCode: new UntypedFormControl(postalCodeValue, [
            Validators.minLength(1),
            Validators.maxLength(255),
          ]),
          street: new UntypedFormControl(streetValue, [
            Validators.minLength(1),
            Validators.maxLength(255),
          ]),
        }),
      );
    } else {
      this.addressForm.addControl(
        controlIndex,
        new UntypedFormGroup({
          country: new UntypedFormControl(countryValue, [Validators.maxLength(255)]),
          city: new UntypedFormControl(cityValue, [Validators.maxLength(255)]),
          postalCode: new UntypedFormControl(postalCodeValue, [Validators.maxLength(255)]),
          street: new UntypedFormControl(streetValue, [Validators.maxLength(255)]),
        }),
      );
    }
  }

  public deletePoint(controlName: string): void {
    this.form.removeControl(controlName);
    this.addressForm.removeControl(controlName);
  }

  public submit(): void {
    if (!this.form.valid || !this.addressForm.valid) {
      return;
    }
    const stops: string[] = Object.values(this.form.value);
    let workSpaceStops: WorkSpaceStop[] = [];
    stops.forEach((name: string, index: number): void => {
      let id: number;
      if (this.existingStopsArray.length > 0) {
        this.existingStopsArray.forEach((stopWithIndex) => {
          if (this.form.controls[stopWithIndex.controlIndex]?.value === name) {
            id = stopWithIndex.stop.id;
          }
        });
      }
      let postalCode: string = this.getAddressForm(`${index}`, 'postalCode')?.value as string;
      postalCode = postalCode ? postalCode : undefined;
      let city: string = this.getAddressForm(`${index}`, 'city')?.value as string;
      city = city ? city : undefined;
      let country: string = this.getAddressForm(`${index}`, 'country')?.value as string;
      country = country ? country : undefined;
      let address: string = this.getAddressForm(`${index}`, 'street')?.value as string;
      address = address ? address : undefined;
      workSpaceStops.push({
        postalCode,
        city,
        country,
        address,
        name,
        id,
      });
    });
    workSpaceStops.find((stop: WorkSpaceStop): void => {
      if (!stop.name) {
        workSpaceStops = undefined;
        return;
      }
    });
    this.submitStops.emit(workSpaceStops);
  }

  public turnOnAddress(key: string): void {
    this.activeAddresses.push(key);
  }

  public getAddressForm(key: string, controlName: string): UntypedFormControl {
    return this.addressForm?.controls[key]?.get(controlName) as UntypedFormControl;
  }

  public translateInstant(name: string): string {
    return this.translate.instant(name) as string;
  }

  public toCharacter(num: number): string {
    return num.toString(36).toUpperCase();
  }

  public getStopLetterFromIndex(stopIndex: number): string {
    return (stopIndex + 10).toString(36);
  }

  public objectKeys(object: Record<string, unknown>): string[] {
    return Object.keys(object);
  }

  private generateForm(): UntypedFormGroup {
    return new UntypedFormGroup({
      0: new UntypedFormControl('', [Validators.maxLength(255), Validators.required]),
      1: new UntypedFormControl('', [Validators.maxLength(255), Validators.required]),
      2: new UntypedFormControl('', [Validators.maxLength(255), Validators.required]),
    });
  }

  private generateAddressForm(): UntypedFormGroup {
    if (this.isComplete) {
      return new UntypedFormGroup({
        0: new UntypedFormGroup({
          country: new UntypedFormControl('', [Validators.minLength(1), Validators.maxLength(255)]),
          city: new UntypedFormControl('', [Validators.minLength(1), Validators.maxLength(255)]),
          postalCode: new UntypedFormControl('', [Validators.minLength(1), Validators.maxLength(255)]),
          street: new UntypedFormControl('', [Validators.minLength(1), Validators.maxLength(255)]),
        }),
        1: new UntypedFormGroup({
          country: new UntypedFormControl('', [Validators.minLength(1), Validators.maxLength(255)]),
          city: new UntypedFormControl('', [Validators.minLength(1), Validators.maxLength(255)]),
          postalCode: new UntypedFormControl('', [Validators.minLength(1), Validators.maxLength(255)]),
          street: new UntypedFormControl('', [Validators.minLength(1), Validators.maxLength(255)]),
        }),
        2: new UntypedFormGroup({
          country: new UntypedFormControl('', [Validators.minLength(1), Validators.maxLength(255)]),
          city: new UntypedFormControl('', [Validators.minLength(1), Validators.maxLength(255)]),
          postalCode: new UntypedFormControl('', [Validators.minLength(1), Validators.maxLength(255)]),
          street: new UntypedFormControl('', [Validators.minLength(1), Validators.maxLength(255)]),
        }),
      });
    } else {
      return new UntypedFormGroup({
        0: new UntypedFormGroup({
          country: new UntypedFormControl('', [Validators.maxLength(255)]),
          city: new UntypedFormControl('', [Validators.maxLength(255)]),
          postalCode: new UntypedFormControl('', [Validators.maxLength(255)]),
          street: new UntypedFormControl('', [Validators.maxLength(255)]),
        }),
        1: new UntypedFormGroup({
          country: new UntypedFormControl('', [Validators.maxLength(255)]),
          city: new UntypedFormControl('', [Validators.maxLength(255)]),
          postalCode: new UntypedFormControl('', [Validators.maxLength(255)]),
          street: new UntypedFormControl('', [Validators.maxLength(255)]),
        }),
        2: new UntypedFormGroup({
          country: new UntypedFormControl('', [Validators.maxLength(255)]),
          city: new UntypedFormControl('', [Validators.maxLength(255)]),
          postalCode: new UntypedFormControl('', [Validators.maxLength(255)]),
          street: new UntypedFormControl('', [Validators.maxLength(255)]),
        }),
      });
    }
  }

  private turnOnValidationForCompletePopup(): void {
    if (this.isComplete) {
      setTimeout((): void => {
        const submitButton: HTMLElement = document.getElementById('SubmitButton');
        if (submitButton) {
          submitButton.click();
        }
      });
    }
  }

  ngAfterViewInit(): void {
    this.turnOnValidationForCompletePopup();
  }
}
